import React, { useState, useEffect, useCallback } from "react";
import Calendar from "./components/Calendar";
import { CalendarProvider, useCalendar } from "./context/CalendarContext";
import { format, getYear } from "date-fns";
import { DayData, DayCapacities, ColorOption, DayColors, AppParameters } from "./types";

// Ícones simples para navegação
const ChevronLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

const SaveIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
  </svg>
);

const FolderOpenIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9.75h16.5m-16.5 0A2.25 2.25 0 015.25 7.5h13.5A2.25 2.25 0 0121 9.75v6.75A2.25 2.25 0 0118.75 18.75H5.25A2.25 2.25 0 013 16.5V9.75z" />
  </svg>
);

const CogIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12a7.5 7.5 0 0015 0m-15 0a7.5 7.5 0 1115 0m-15 0H3m18 0h-1.5m-15.036-7.126A9 9 0 017.5 4.5m11.036 3.374A9 9 0 0016.5 4.5m-1.5 15a7.5 7.5 0 00-15 0m15 0a7.5 7.5 0 11-15 0m15 0H18M3.75 12H6m10.5 0h-1.5m-12.536 7.126A9 9 0 017.5 19.5m11.036-3.374A9 9 0 0016.5 19.5m-1.5-15a7.5 7.5 0 00-15 0m15 0H18" />
  </svg>
);

const DocumentTextIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

const RefreshIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

const ExportCsvIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25" />
    </svg>
);

const ExportButton: React.FC = () => {
  const { calendarData, currentYear, getInitialDayData } = useCalendar();
  const handleExportCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,data,tipo\n";
    const yearDataToExport: Record<string, DayData> = {};
    let hasCustomDataForYear = false;
    for (const dateString in calendarData) {
      if (getYear(calendarData[dateString].date) === currentYear) {
        hasCustomDataForYear = true;
        yearDataToExport[dateString] = calendarData[dateString];
      }
    }
    if (hasCustomDataForYear) {
      Object.keys(yearDataToExport).sort().forEach(dateString => {
        const day = yearDataToExport[dateString];
        const formattedDate = format(day.date, "dd/MM/yyyy");
        csvContent += `${formattedDate},"${day.type}"\n`;
      });
    } else {
      for (let i = 0; i < 366; i++) {
        const currentDate = new Date(currentYear, 0, 1 + i);
        if (currentDate.getFullYear() > currentYear) break;
        const day = getInitialDayData(currentDate);
        const formattedDate = format(day.date, "dd/MM/yyyy");
        csvContent += `${formattedDate},"${day.type}"\n`;
      }
    }
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `calendario_${currentYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return (
    <button
      onClick={handleExportCSV}
      className="px-2.5 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors duration-150 shadow flex items-center"
    >
      <ExportCsvIcon /> Exportar CSV
    </button>
  );
};

const AutomaticSuggestionButton: React.FC = () => {
  const { applyAutomaticSuggestions } = useCalendar();
  const [isLoading, setIsLoading] = useState(false);

  const handleAutomaticSuggestion = async () => {
    setIsLoading(true);
    // Simulate processing time, similar to AI button but can be shorter if logic is quick
    await new Promise(resolve => setTimeout(resolve, 300)); 
    applyAutomaticSuggestions();
    setIsLoading(false);
  };

  return (
    <button
      onClick={handleAutomaticSuggestion}
      disabled={isLoading}
      className="px-2.5 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors duration-150 shadow flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {isLoading ? (
        <>
          <svg className="animate-spin -ml-0.5 mr-1.5 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Processando...
        </>
      ) : (
        <>
          {/* Ícone opcional para o botão Automático, pode ser um robô simples ou similar */}
          {/* <svg ... /> */}
          Automático
        </>
      )}
    </button>
  );
};

const AISuggestionButton: React.FC = () => {
  const { applyOpenAISuggestions, appParams } = useCalendar();
  const [isLoading, setIsLoading] = useState(false);

  const handleAISuggestions = async () => {
    setIsLoading(true);
    const storedApiKey = localStorage.getItem("sim_calendario_apiKey");
    if (!storedApiKey) {
        alert("Chave API da OpenAI não configurada. Por favor, configure-a no menu Salvar/Abrir e clique em Salvar.");
        setIsLoading(false);
        return;
    }
    // Adiciona um pequeno delay para simular a chamada de API, mesmo que a lógica seja mockada
    // Isso melhora a percepção do usuário de que algo está acontecendo.
    await new Promise(resolve => setTimeout(resolve, 500)); 
    await applyOpenAISuggestions(storedApiKey);
    setIsLoading(false);
  };
  return (
    <button
      onClick={handleAISuggestions}
      disabled={isLoading}
      className="px-2.5 py-1 text-xs font-medium text-white rounded shadow-md flex items-center disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
      style={{
        background: isLoading ? "#a855f7" : "linear-gradient(135deg, #f97316, #ec4899, #8b5cf6)",
        border: "1px solid transparent", // Borda mais sutil
      }}
    >
      {isLoading ? (
        <>
          <svg className="animate-spin -ml-0.5 mr-1.5 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Calculando...
        </>
      ) : (
        <>
          <span role="img" aria-label="stars" className="mr-1 text-md">💫</span>
          Sugestão AI
        </>
      )}
    </button>
  );
};

const YearNavigator: React.FC = () => {
  const { currentYear, setCurrentYear } = useCalendar();
  return (
    <div className="flex items-center justify-center my-3">
      <button 
        onClick={() => setCurrentYear(currentYear - 1)} 
        className="p-1.5 rounded-full hover:bg-gray-200 transition-colors duration-150 text-gray-600 hover:text-gray-800"
        aria-label="Ano Anterior"
      >
        <ChevronLeftIcon />
      </button>
      <h2 className="text-xl font-semibold mx-3 w-20 text-center text-gray-700">{currentYear}</h2>
      <button 
        onClick={() => setCurrentYear(currentYear + 1)} 
        className="p-1.5 rounded-full hover:bg-gray-200 transition-colors duration-150 text-gray-600 hover:text-gray-800"
        aria-label="Próximo Ano"
      >
        <ChevronRightIcon />
      </button>
    </div>
  );
}

const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-green-500">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const XCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-red-500">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const LoadingSpinnerIcon = () => (
  <svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const MenuBar: React.FC = () => {
  const { appParams, updateAppParams, getAIDefaultPrompt, resetCalendarState } = useCalendar();
  const [isSaving, setIsSaving] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeySaved, setApiKeySaved] = useState(false);
  const [apiKeyValidationStatus, setApiKeyValidationStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [isValidatingApiKey, setIsValidatingApiKey] = useState(false); // Para controlar o estado de loading da validação

  const [showParamsModal, setShowParamsModal] = useState(false);
  // State for editable dayHours and unidadesPorHora in the modal
  const [editableDayHours, setEditableDayHours] = useState<Record<ColorOption, number>>(appParams.dayHours);
  const [editableUnidadesPorHora, setEditableUnidadesPorHora] = useState<number>(appParams.unidadesPorHora);

  const [showPromptModal, setShowPromptModal] = useState(false);
  const [editablePrompt, setEditablePrompt] = useState(appParams.aiPrompt);
  const [editableModel, setEditableModel] = useState(appParams.selectedOpenAIModel);

  const validateApiKey = async (keyToValidate: string): Promise<boolean> => {
    if (!keyToValidate) {
      setApiKeyValidationStatus("idle");
      return false;
    }
    setIsValidatingApiKey(true);
    setApiKeyValidationStatus("validating");
    try {
      const response = await fetch("https://api.openai.com/v1/models", {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${keyToValidate}`,
        },
      });
      if (response.ok) {
        // const data = await response.json(); // Optionally process data
        setApiKeyValidationStatus("valid");
        return true;
      } else {
        // console.error("API Key validation failed:", response.status, await response.text());
        setApiKeyValidationStatus("invalid");
        return false;
      }
    } catch (error) {
      console.error("Error validating API key:", error);
      setApiKeyValidationStatus("invalid"); // Consider network errors as invalid
      return false;
    } finally {
      setIsValidatingApiKey(false);
    }
  };


  const handleApiKeyInputChange = (newKeyValue: string) => {
    setApiKey(newKeyValue);
    setApiKeySaved(false); // Mark as unsaved when user types
    if (newKeyValue === "") {
      setApiKeyValidationStatus("idle"); // Reset validation if key is cleared
    } else {
      // Optionally, could trigger validation on debounce here, or wait for save
      setApiKeyValidationStatus("idle"); // Or 'validating' if debounce validation is added
    }
  };

  const handleSaveAppSettings = async () => {
    setIsSaving(true);
    let keyIsValid = false;
    if (apiKey) {
      localStorage.setItem("sim_calendario_apiKey", apiKey);
      keyIsValid = await validateApiKey(apiKey); // Validate before confirming save visually
      setApiKeySaved(true);
    } else {
      localStorage.removeItem("sim_calendario_apiKey");
      setApiKeyValidationStatus("idle");
      setApiKeySaved(false);
    }
    
    // updateAppParams({ apiKey: apiKey }); // Update context if apiKey is part of appParams
    // For now, apiKey is managed locally in MenuBar and read from localStorage by AISuggestionButton

    setTimeout(() => {
      setIsSaving(false);
      // Feedback alerts can be added here if desired, or rely on visual icon
      console.log(`Configurações salvas. Chave API: ${apiKey ? (keyIsValid ? "Válida" : "Inválida ou não verificada") : "Não configurada"}`);
    }, 300); // Shorter timeout now that validation happens before
  };

  useEffect(() => {
    const storedApiKey = localStorage.getItem("sim_calendario_apiKey");
    if (storedApiKey) {
      setApiKey(storedApiKey);
      setApiKeySaved(true);
      validateApiKey(storedApiKey); // Validate on load
    } else {
      setApiKeyValidationStatus("idle");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only on mount

  // Update local state for modal when appParams change from context
  useEffect(() => {
    setEditableDayHours(appParams.dayHours);
    setEditableUnidadesPorHora(appParams.unidadesPorHora);
  }, [appParams.dayHours, appParams.unidadesPorHora]);

  useEffect(() => {
    setEditablePrompt(appParams.aiPrompt);
    setEditableModel(appParams.selectedOpenAIModel);
  }, [appParams.aiPrompt, appParams.selectedOpenAIModel]);

  // const handleSaveAppSettings = () => { // Esta é a duplicata a ser removida
  //   setIsSaving(true);
  //   if (apiKey) {
  //     localStorage.setItem("sim_calendario_apiKey", apiKey);
  //     setApiKeySaved(true);
  //   } else {
  //     localStorage.removeItem("sim_calendario_apiKey");
  //     setApiKeySaved(false);
  //   }
  //   setTimeout(() => {
  //     setIsSaving(false);
  //     console.log("Configurações salvas (Chave API no localStorage)");
  //   }, 1000);
  // };

  const handleParamsSave = () => {
    updateAppParams({ dayHours: editableDayHours, unidadesPorHora: editableUnidadesPorHora });
    setShowParamsModal(false);
  };

  const handleDayHoursChange = (colorKey: ColorOption, value: string) => {
    const newHours = parseFloat(value);
    if (!isNaN(newHours) && newHours >= 0) {
      setEditableDayHours(prev => ({ ...prev, [colorKey]: newHours }));
    }
  };

  const handleUnidadesPorHoraChange = (value: string) => {
    const newUnits = parseInt(value, 10);
    if (!isNaN(newUnits) && newUnits >= 0) {
      setEditableUnidadesPorHora(newUnits);
    }
  };

  // Calculate capacities for display in modal based on local modal state
  const modalDisplayCapacities = React.useMemo(() => {
    const capacities: Record<ColorOption, number> = {} as Record<ColorOption, number>; 
    for (const key in editableDayHours) {
        const colorKey = key as ColorOption;
        capacities[colorKey] = editableDayHours[colorKey] * editableUnidadesPorHora;
    }
    return capacities;
  }, [editableDayHours, editableUnidadesPorHora]);

  const handlePromptSave = () => {
    updateAppParams({ aiPrompt: editablePrompt, selectedOpenAIModel: editableModel });
    setShowPromptModal(false);
  };

  const openAIModels = ["gpt-3.5-turbo", "gpt-4", "gpt-4-turbo-preview", "gpt-4-mini"];

  return (
    <div className="w-full bg-gray-50 p-2 mb-4 shadow rounded-lg border border-gray-200">
      <div className="container mx-auto flex flex-wrap items-center justify-between gap-y-2 gap-x-1">
        {/* Grupo Esquerda */}
        <div className="flex items-center space-x-1">
          <div className="relative">
            <select 
              className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent appearance-none pr-5"
              defaultValue="TODOS"
            >
              <option value="TODOS">Todos</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1 text-gray-700">
              <svg className="fill-current h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
            </div>
          </div>
          <button 
            onClick={handleSaveAppSettings}
            disabled={isSaving}
            className="px-2.5 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-150 shadow flex items-center disabled:opacity-50"
          >
            {isSaving ? (
              <svg className="animate-spin -ml-0.5 mr-1.5 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            ) : <SaveIcon /> }
            {isSaving ? "Salvando..." : "Salvar"}
          </button>
          <button className="px-2.5 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors duration-150 shadow flex items-center"><FolderOpenIcon />Abrir</button>
          <button 
            onClick={() => {
              if (window.confirm("Tem certeza que deseja reiniciar o calendário? Todas as alterações e sugestões não salvas serão perdidas.")) {
                resetCalendarState();
              }
            }}
            className="px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors duration-150 shadow flex items-center"
          >
            <RefreshIcon />Reiniciar
          </button>
          <button onClick={() => setShowParamsModal(true)} className="px-2.5 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors duration-150 shadow flex items-center"><CogIcon />Parâmetros</button>
          <ExportButton />
          <AutomaticSuggestionButton /> 
        </div>

        {/* Grupo Direita - Sugestão IA, Chave e Instruções */}
        <div className="flex items-center space-x-1">
          <AISuggestionButton /> 
          <div className="flex-shrink-0 w-40 relative"> {/* Ajuste a largura e adicione relative */}
            <label htmlFor="apiKey" className="sr-only">Chave IA</label>
            <input 
              type={showApiKey ? "text" : "password"} 
              id="apiKey" 
              value={apiKey}
              onChange={(e) => handleApiKeyInputChange(e.target.value)}
              className={`px-2 py-1 w-full text-xs border rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent ${apiKeyValidationStatus === "invalid" && apiKey ? "border-red-500" : "border-gray-300"}`}
              placeholder="Chave API OpenAI"
            />
            <div className="absolute inset-y-0 right-0 pr-8 flex items-center">
              {isValidatingApiKey ? (
                <LoadingSpinnerIcon />
              ) : apiKey && apiKeyValidationStatus === "valid" ? (
                <CheckCircleIcon />
              ) : apiKey && apiKeyValidationStatus === "invalid" ? (
                <XCircleIcon />
              ) : null}
            </div>
            <button 
              type="button"
              onClick={() => setShowApiKey(!showApiKey)}
              className="absolute inset-y-0 right-0 px-1.5 flex items-center text-xs text-gray-500 hover:text-gray-700"
              title={showApiKey ? "Ocultar Chave" : "Mostrar Chave"}
            >
              {showApiKey ? 
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.243 4.243L6.228 6.228" /></svg> :
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              }
            </button>
          </div>
          <button onClick={() => setShowPromptModal(true)} className="px-2.5 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors duration-150 shadow flex items-center"><DocumentTextIcon />Instruções IA</button>
        </div>
      </div>

      {/* Modal de Parâmetros */}
      {showParamsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white p-5 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"> {/* Increased max-h */}
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Parâmetros da Aplicação</h3>
            
            <div className="mb-4">
              <label htmlFor="unidadesPorHoraModal" className="block text-sm font-medium text-gray-700 mb-1">Unidades por Hora:</label>
              <input 
                type="number"
                id="unidadesPorHoraModal"
                value={editableUnidadesPorHora}
                onChange={(e) => handleUnidadesPorHoraChange(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                min="0"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 border-b pb-1">Horas por Tipo de Dia:</h4>
                {Object.keys(DayColors).map(key => {
                  const colorKey = key as ColorOption;
                  return (
                    <div key={`hours-edit-${colorKey}`} className="mb-2.5 flex items-center justify-between">
                      <label htmlFor={`hours-${colorKey}`} className="block text-xs text-gray-600 mr-2 truncate" title={DayColors[colorKey].meaning}>
                        {DayColors[colorKey].meaning}:
                      </label>
                      <input 
                        type="number"
                        id={`hours-${colorKey}`}
                        value={editableDayHours[colorKey] === undefined ? DayColors[colorKey].hours : editableDayHours[colorKey]}
                        onChange={(e) => handleDayHoursChange(colorKey, e.target.value)}
                        className="mt-1 block w-24 px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-xs text-right"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  );
                })}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 border-b pb-1">Capacidade (Unidades/Dia):</h4>
                {Object.keys(DayColors).map(key => {
                  const colorKey = key as ColorOption;
                  return (
                    <div key={`cap-display-${colorKey}`} className="mb-2.5 flex items-center justify-between">
                      <span className="block text-xs text-gray-600 mr-2 truncate" title={DayColors[colorKey].meaning}>
                        {DayColors[colorKey].meaning}:
                      </span>
                      <input 
                        type="text" 
                        value={(modalDisplayCapacities[colorKey] || 0).toLocaleString("pt-BR")}
                        readOnly
                        className="mt-1 block w-24 px-2 py-1 border border-gray-200 bg-gray-100 rounded-md shadow-sm sm:text-xs text-right text-gray-500 cursor-default"
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-3 border-t mt-4">
              <button 
                onClick={() => {
                  setShowParamsModal(false);
                  // Reset local modal state to actual appParams if user cancels
                  setEditableDayHours(appParams.dayHours);
                  setEditableUnidadesPorHora(appParams.unidadesPorHora);
                }} 
                className="px-4 py-2 text-xs font-medium bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
              >
                Cancelar
              </button>
              <button 
                onClick={handleParamsSave} 
                className="px-4 py-2 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Salvar Parâmetros
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Instruções IA */}
      {showPromptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-5 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <h3 className="text-lg font-semibold mb-3">Instruções para IA e Seleção de Modelo</h3>
            <div className="mb-3">
              <label htmlFor="openaiModelSelect" className="block text-sm font-medium text-gray-700 mb-1">Modelo OpenAI:</label>
              <select 
                id="openaiModelSelect"
                value={editableModel}
                onChange={(e) => setEditableModel(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none"
              >
                {openAIModels.map(model => (
                  <option key={model} value={model}>{model}</option>
                ))}
              </select>
            </div>
            <label htmlFor="aiPromptTextarea" className="block text-sm font-medium text-gray-700 mb-1">Prompt para IA:</label>
            <textarea 
              id="aiPromptTextarea"
              value={editablePrompt}
              onChange={(e) => setEditablePrompt(e.target.value)}
              className="w-full flex-grow px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[200px] resize-y"
              rows={10}
            />
            <div className="mt-4 flex justify-end space-x-2">
              <button onClick={() => { setShowPromptModal(false); setEditablePrompt(appParams.aiPrompt); setEditableModel(appParams.selectedOpenAIModel); }} className="px-3 py-1.5 text-xs text-gray-700 bg-gray-200 rounded hover:bg-gray-300 transition-colors">Cancelar</button>
              <button onClick={handlePromptSave} className="px-3 py-1.5 text-xs text-white bg-blue-500 rounded hover:bg-blue-600 transition-colors">Salvar Instruções e Modelo</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


const AppContent: React.FC = () => {
  const { currentYear } = useCalendar();
  return (
    <div className="container mx-auto p-2 md:p-4 max-w-full">
      <h1 className="text-2xl font-bold text-center text-gray-800 mb-2">Simulação de Calendário</h1>
      <MenuBar />
      <YearNavigator />
      <Calendar key={currentYear} />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <CalendarProvider>
      <AppContent />
    </CalendarProvider>
  );
};

export default App;